import { Pluviometers } from '../../shared/constants/pluviometer.constant';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { catchError, forkJoin, map, of, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'app-pluviometer',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, HttpClientModule],
  templateUrl: './pluviometer.component.html'
})
export class PluviometerComponent {

  @Input()
  public slug!: string;

  public pluviometers: any[] = Pluviometers;
  public pluviometer: any = null;
  public sensor: any;
  public show: boolean = false;
  public emptyState: boolean = false;
  private _slug: any;

  private _subscription: Subscription = new Subscription();

  constructor(private route: ActivatedRoute,
              private _cdr: ChangeDetectorRef,
              private _http: HttpClient) {
  }

  ngOnInit() {
    this._getSlugParam();
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private async _getSlugParam() {
    if (this.slug) {
      this._slug = this.slug;
    } else {
      this._slug = this.route.snapshot.paramMap.get('id');
    }

    await this._getData();
  }

  public async reloadData() {
    this._cdr.detectChanges();

    this._getSlugParam();
  }

  private async _getData(): Promise<void> {
    this.pluviometer = null;
    const pluviometer = this.pluviometers.find(pluviometer => pluviometer.slug.toLowerCase() === this._slug);

    if (pluviometer) {
      const requests = {
        temperature: this._http.get(`${pluviometer.baseLink}?startDate=latest&node=${pluviometer.temperature}&locale=es&units=metric`).pipe(
          map((data: any) => ({ value: data.formattedValue, message: this._getMessage(data.rawValue, 'temperature') })),
          catchError(() => of(null))
        ),
        precipitation: this._http.get(`${pluviometer.baseLink}?startDate=latest&node=${pluviometer.precipitation}&locale=es&units=metric`).pipe(
          map((data: any) => ({ value: data.formattedValue, message: this._getMessage(data.rawValue, 'precipitation') })),
          catchError(() => of(null))
        ),
        relativeHumidity: this._http.get(`${pluviometer.baseLink}?startDate=latest&node=${pluviometer.relativeHumidity}&locale=es&units=metric`).pipe(
          map((data: any) => ({ value: data.formattedValue, message: this._getMessage(data.rawValue, 'relativeHumidity') })),
          catchError(() => of(null))
        ),
      };

      forkJoin(requests).subscribe(results => {
        const hasData = Object.values(results).some(result => result !== null);

        if (hasData) {
          this.pluviometer = {
            name: pluviometer.name,
            temperature: results.temperature ? results.temperature.value : null,
            temperatureMessage: results.temperature ? results.temperature.message : null,
            precipitation: results.precipitation ? results.precipitation.value : null,
            precipitationMessage: results.precipitation ? results.precipitation.message : null,
            relativeHumidity: results.relativeHumidity ? results.relativeHumidity.value : null,
            relativeHumidityMessage: results.relativeHumidity ? results.relativeHumidity.message : null,
          };
          this.show = true;
        } else {
          console.error('No data available for any attributes');
          this.show = false;
          this.pluviometer = null;
          this.emptyState = true;
        }

        this.show = true;
        this._cdr.detectChanges();
      }, error => {
        console.error('Error fetching data:', error);
        this.show = false;
        this._cdr.detectChanges();
      });
    } else {
      this.show = false;
      console.error(`No pluviometer found for slug: ${this._slug}`);
      this._cdr.detectChanges();
    }
  }

  private _getMessage(value: string, attribute: string) {
    const numericValue = parseFloat(value);
    let message = {};

    switch (attribute) {
      case 'temperature':
        message = this._getTemperatureMessage(numericValue);
        break;
    
      case 'precipitation':
        message = this._getPrecipitationMessage(numericValue);
        break;

      case 'relativeHumidity':
        message = this._getRelativeHumidityMessage(numericValue);
        break;
    }
    
    return message;
  }

  private _getTemperatureMessage(value: number): string {
    if (value < 0) {
      return '¡Hace mucho frío! Abrígate bien.';
    } else if (value < 10) {
      return 'Hace frío, lleva una chaqueta.';
    } else if (value < 20) {
      return '¡Un clima fresco perfecto para pasear!';
    } else if (value < 25) {
      return 'El clima es templado y agradable.';
    } else if (value < 30) {
      return 'Hace calor, busca un lugar fresco.';
    } else {
      return '¡El calor es intenso! Mantente hidratado.';
    }
  }
  
  private _getPrecipitationMessage(value: number): string {
    if (value === 0) {
      return 'No hay lluvia en este momento.';
    } else if (value < 2) {
      return 'Hay llovizna, lleva un paraguas.';
    } else if (value < 7.6) {
      return 'Lluvia ligera, lleva una chaqueta impermeable.';
    } else if (value < 50) {
      return 'Lluvia moderada, cuidado al conducir.';
    } else if (value < 100) {
      return 'Lluvia fuerte, mantente seguro en casa.';
    } else {
      return 'Lluvia intensa, precaución ante posibles inundaciones.';
    }
  }
  
  private _getRelativeHumidityMessage(value: number): string {
    if (value < 30) {
      return 'El ambiente está muy seco, hidrátate bien.';
    } else if (value < 50) {
      return 'El ambiente es seco, bebe suficiente agua.';
    } else if (value < 70) {
      return 'La humedad es agradable, disfruta el día.';
    } else if (value < 90) {
      return 'El ambiente es húmedo, prepárate para el calor.';
    } else {
      return 'El ambiente está muy húmedo, cuidado con el moho.';
    }
  }
}
